// Asistencia.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const HOSTNAMEAPI = "mixta39.dev/phpapi";

const API_URL = `https://${HOSTNAMEAPI}/api/estudiantes.php`;
const SAVE_API_URL = `https://${HOSTNAMEAPI}/api/saveScan.php`;
const defaultImage = '/images/default-profile.png';

/** ===== Helpers de fecha (LOCAL) ===== */
function pad(n) { return String(n).padStart(2, '0'); }

/** ISO local sin zona: YYYY-MM-DDTHH:mm:ss (no UTC) */
function localISO(date = new Date()) {
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
}

/** Para mostrar al usuario, en su idioma/zona local */
function formatDateForUser(date, locale = navigator.language || 'es-MX') {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}

const Asistencia = () => {
  const [studentId, setStudentId] = useState('');
  const [error, setError] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanTime, setScanTime] = useState(null);      // texto amigable para UI
  const [saveStatus, setSaveStatus] = useState(null);  // resultado de guardado
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('user');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const data = e.target.value;
    const isValidId = /^[0-9]+$/.test(data);

    if (isValidId) {
      setStudentId(data);
      setError(null);
    } else if (data !== "") {
      setError('Solo se permiten números en el ID.');
    } else {
      setError(null);
      setStudentId('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (!studentId) {
        setError('El ID no puede estar vacío.');
        return;
      }

      setError(null);
      setLoading(true);

      // Capturamos el ID escaneado antes de limpiar el input
      const scannedId = studentId;

      // Obtener datos del estudiante
      fetch(`${API_URL}?EstudianteID=${encodeURIComponent(scannedId)}`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.json();
        })
        .then(data => {
          setLoading(false);
          setStudentId(''); // limpiar input

          if (data) {
            setStudentData(data);

            // Hora local para UI
            const now = new Date();
            setScanTime(formatDateForUser(now));

            // Hora local ISO (sin zona) para backend
            const localTimeISO = localISO(now);

            // Guardar escaneo solo si se encontró el estudiante
            return fetch(SAVE_API_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                EstudianteID: scannedId,
                ScanTime: localTimeISO,   // <-- local, no UTC
              })
            })
              .then(response => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
              })
              .then(() => setSaveStatus('Escaneo guardado exitosamente.'))
              .catch(err => {
                console.error('Error al guardar los datos del escaneo:', err);
                setSaveStatus('Hubo un error al guardar los datos del escaneo.');
              });

          } else {
            setStudentData(null);
            setScanTime(null);
            setError('No se encontró el estudiante con ese ID.');
          }
        })
        .catch(err => {
          console.error(err);
          setLoading(false);
          setError('Hubo un error al obtener los datos del estudiante.');
          setStudentId('');
          setStudentData(null);
          setScanTime(null);
        });
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="text-center mb-4">Escanear ID del Estudiante</h2>

      <div className="d-flex justify-content-center mb-4">
        <input
          type="text"
          value={studentId}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder="Escanea el ID (solo números) y presiona Enter"
          className="form-control form-control-lg"
          autoFocus
        />
      </div>

      <div className="mt-4">
        <h4>ID del Estudiante Escaneado:</h4>
        <p>{studentId ? studentId : 'Esperando escaneo...'}</p>
      </div>

      {error && <div className="text-danger mt-2">{error}</div>}
      {saveStatus && <div className="text-success mt-2">{saveStatus}</div>}

      {loading && (
        <div className="d-flex justify-content-center mt-2">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      )}

      {studentData && (
        <div className="mt-4 p-3 shadow-sm bg-white rounded">
          <h4>Datos del Estudiante:</h4>
          <div className="d-flex align-items-start">
            <img
              src={studentData.Imagen || defaultImage}
              alt="Foto del Estudiante"
              className="img-thumbnail mr-3"
              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
            />
            <table className="table table-bordered table-striped">
              <tbody>
                <tr>
                  <th>Nombre</th>
                  <td>{studentData.Nombre}</td>
                </tr>
                <tr>
                  <th>Apellido Paterno</th>
                  <td>{studentData.ApellidoPaterno}</td>
                </tr>
                <tr>
                  <th>Apellido Materno</th>
                  <td>{studentData.ApellidoMaterno}</td>
                </tr>
                <tr>
                  <th>Edad</th>
                  <td>{studentData.Edad}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {scanTime && studentData && (
        <div className="mt-3 text-center" style={{ fontSize: '150%' }}>
          <p><strong>Fecha y Hora del Escaneo:</strong> {scanTime}</p>
        </div>
      )}
    </div>
  );
};

export default Asistencia;
